import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { Transaction } from "./transactions";

/**
 * One month's money in and money out (`model.md` §4.1's arithmetic, summed over
 * headers).
 *
 * `through` is the last day counted. For the month still running it is today,
 * not the month's end — which is what makes the comparison against last month
 * like-for-like rather than a comparison between a part and a whole.
 */
export interface CashFlow {
  period_start: string;
  period_end: string;
  through: string;
  /** Positive. */
  in_minor: number;
  /** Negative, as `model.md` §2.2 rule 1 holds it. */
  out_minor: number;
  net_minor: number;
  /** The outgoing half as a magnitude, for a label that says "ausgegeben". */
  spent_minor: number;
  lines: number;
  is_complete: boolean;
}

/** The answer to "am I okay this month?". */
export interface Trend {
  current: CashFlow;
  /** The same stretch of last month — to the same day number, not its total. */
  previous: CashFlow;
  delta_minor: number;
  /** -1, 0 or +1, decided by the server so two screens cannot disagree. */
  direction: number;
  /** False when last month has nothing in it, and no arrow should be drawn. */
  is_meaningful: boolean;
}

export interface CategoryTotal {
  category_id: string;
  name: string;
  /** A positive magnitude, rolled up over the category and its descendants. */
  spent_minor: number;
  lines: number;
}

/** `model.md` §4.4. */
export interface NetPosition {
  total_minor: number;
  currency_code: string;
  accounts: number;
  converted: number;
  as_of: string;
  /** True when a rate is missing, in which case there is no total to show. */
  unavailable: boolean;
  missing_pair?: string;
}

export interface Dashboard {
  trend: Trend;
  /** Oldest first, every month present — a quiet month is zero, not absent. */
  history: CashFlow[];
  top_categories: CategoryTotal[];
  net_position: NetPosition;
  recent: Transaction[];
}

export const dashboardQueryKey = (householdId: string) =>
  ["households", householdId, "dashboard"] as const;

/**
 * The whole screen in one request.
 *
 * Six requests would render six moments — the balance from one instant beside a
 * category total from another — and on a slow connection the household would
 * watch the answer assemble itself out of order.
 */
export function useDashboard(householdId: string | undefined, asOf?: string) {
  const query = asOf ? `?as_of=${asOf}` : "";
  return useQuery({
    queryKey: [...dashboardQueryKey(householdId ?? ""), asOf ?? "today"],
    queryFn: ({ signal }) =>
      apiFetch<Dashboard>(`/api/v1/households/${householdId}/dashboard${query}`, { signal }),
    enabled: Boolean(householdId),
  });
}

/** A period key as a short German month, for an axis label. */
